import React, { useState } from 'react';
import { useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Navbar, Nav, Placeholder } from 'react-bootstrap';
import config from '@/config';
import { useLinksContext } from '@/context/LinksContext';
import { useOverviewContext } from '@/context/OverviewContext';
import pdfElementarySchool from '@/assets/pdfs/Elementary-School-Guide.pdf';
import pdfMiddleSchool     from '@/assets/pdfs/Middle-School-Guide.pdf';
import pdfHighSchool       from '@/assets/pdfs/High-School-Guide.pdf';
import imgStrider from "@/assets/images/canvas-landing/Strider-cropped.png";
import imgSunrays from "@/assets/images/canvas-landing/Sunrays-rand2.svg";

import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';

const CanvasLandingMilestone: React.FC = () => {
    const { myAccountLink } = useLinksContext();
    const { k12SchoolHref, showK12SchoolCta, isReady: k12PortalReady } = useTasklistDisplayRuntime();
    const { identityId, students } = useOverviewContext(); 
    const { oktaAuth, authState } = useOktaAuth();
    const [loading, setLoading] = useState(true);

    const getAccountLink = () => {
        try {
          if (!myAccountLink) {
            console.warn("My Account link is missing.");
            return;
          }
          const cleanUrl = myAccountLink.trim().replace(/^"|"$/g, ""); 
          window.open(cleanUrl, "_blank");
        } catch (error) {
          console.error("Failed to open My Account link:", error);
        }
    };
    const handleLogout = async () => {
        try {
          await oktaAuth.signOut({
            postLogoutRedirectUri: config.oidc.postLogoutRedirectUri, 
          });
          sessionStorage.removeItem("authToken");
          sessionStorage.removeItem("canvas");
        } catch (error) {
          console.error("Logout failed", error);
        }
    };
    const getCanvasPortalURL = () => {
        try {
          if (!k12SchoolHref) {
            console.warn("canvasURL is missing from API");
            return;
          }
          const cleanUrl = k12SchoolHref.trim().replace(/^"|"$/g, ""); 
          window.open(cleanUrl, "_blank");
        } catch (error) {
          console.error("Failed to open canvasLink:", error);
        }
    };

    useEffect(() => {
        document.body.classList.add('landing-page');
        return () => {
          document.body.classList.remove('landing-page');
        };
    }, []);

    useEffect(() => {
    if (students) {
      setLoading(false);
    }
  }, [students]);
    return(
        <div className="canvas-landing">
            <header>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end align-items-center">
                                <Navbar className="seconday-menu">
                                    <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                                        <Navbar.Collapse id="responsive-navbar-nav">
                                        <Nav className="ms-auto">
                                            <Nav.Link id="acct" onClick={getAccountLink} className="nav-item">
                                                Account
                                            </Nav.Link>

                                            <Nav.Link id="logout" onClick={handleLogout} className="nav-item">
                                                Logout
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar.Collapse>
                                </Navbar>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
             
            <main>
                <div className="content-section">
                    <div id="background" className="sunrays-container">
                        <img
                            id="sunrays"
                            className="rotating"
                            src={imgSunrays}
                            alt=""
                            style={{ width: "165vmax", height: "165vmax" }}
                        />
                        <div id="vista-wrapper">
                            <img
                            id="strider"
                            src={imgStrider}
                            alt="Strider the fox in hiking gear, overlooking the vista"
                            style={{ width: "26.5cqw", height: "auto" }}
                            />
                        </div>
                    </div>
                    <div className="content-grid">
                        <div id="content-wrapper">
                            <h1 className='title'>Welcome! We're glad you're here.</h1>
                            <div className="student-boxes">
                                {loading ? (
                                    Array(students?.length || 1).fill(null).map((_, index) => (
                                        <div className="student-school-info" key={index}>
                                        <div className="student-name">
                                            <Placeholder animation="glow">
                                            <Placeholder xs={8} />
                                            </Placeholder>
                                        </div>
                                        <div className="school-name">
                                            <Placeholder animation="glow">
                                            <Placeholder xs={6} />
                                            </Placeholder>
                                        </div>
                                        </div>
                                    ))
                                ) : students.length > 0 ? (
                                    students.map((student: any, index: number) => (
                                        <div className="student-school-info" key={index}>
                                        <div className="student-name">
                                            {student.firstName} {student.lastName}
                                        </div>
                                        <div className="school-name">{student.schoolName}</div>
                                        </div>
                                    ))
                                ): (
                                    <p data-dev-note="No Student Info Available"></p>
                                )}
                             
                            </div>

                            <div className="title"></div>
                            <h2 className='sub-title'>Congratulations on getting to this first enrollment milestone!</h2>
                            <div className='btn-div mb-3'>
                                {k12PortalReady && showK12SchoolCta && k12SchoolHref ? 
                                    (<>
                                        <p className='description'>Our new Online School experience is ready! Learn your way around before school starts.</p>
                                        
                                        <Nav.Link className="canvas-btn" id="ols" onClick={getCanvasPortalURL}>
                                            Go to OLS
                                        </Nav.Link>
                                    </>)
                                    : (<>
                                        <p className='description'> Now that your enrollment status is up-to-date, you can take a short break while we work behind the scenes to set up your school experience. We'll update this page to let you know when it's time for your next steps. 
                                        </p>
                                        <p className='description'> But for now, sit back, relax, and enjoy the view!
                                        </p>
                                    </>)
                                }
                            </div>
                            <div className="pdf-links">
                                {k12PortalReady && showK12SchoolCta && k12SchoolHref ? 
                                (<>
                                    <p className="description">Or check out our survival guides before jumping into your new school environment: </p>
                                </>)
                                : (<>
                                    <p className="description">To get a head start on what's ahead, check out these cool resources:</p>
                                </>)
                                }
    
                                <ul>
                                    <li><a href={pdfElementarySchool} target="_blank" rel="noopener noreferrer">Elementary School Survival Guide </a></li>
                                    <li><a href={pdfMiddleSchool} target="_blank" rel="noopener noreferrer">Middle School Survival Guide</a></li>
                                    <li><a href={pdfHighSchool} target="_blank" rel="noopener noreferrer">High School Survival Guide</a></li>
                                </ul>
                            </div>

                        </div>
                    </div>
                </div>

            </main>
           
           <footer>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-6">
                            <ul className="footer-links">
                                <li><a target="_blank" href="https://www.k12.com/terms-of-use/">Terms</a></li>
                                <li><a target="_blank" href="https://www.stridelearning.com/privacy-policy/">Privacy</a></li>
                                <li><a target="_blank" href="https://www.k12.com/ip-policy/">Copyright</a></li>
                                <li><a target="_blank" href="https://www.k12.com/accessibility/">Accessibility</a></li>
                            </ul>
                        </div>
                        <div className="col-lg-6">
                            <div className="copyright text-lg-end">
                                Copyright © 2025 Stride, Inc. All rights reserved.
                            </div>
                        </div>
                    </div>
                </div>
           </footer>
            
        </div>
    );

};
export default CanvasLandingMilestone;